import dayjs from 'dayjs';
import { DEFAULT_ADDRESS } from '@/utilities/geography';

export default class Applicant {
  prefix = '';
  firstName = '';
  lastName = '';
  suffix = '';
  nonProfessional = true;
  permanentResident = false;
  dateOfBirth = '';
  citizenshipCountry = 'USA';
  socialSecurityNumber = '';
  employmentStatus = 'UNEMPLOYED';
  employer = '';
  positionEmployed = null;
  homeAddress = DEFAULT_ADDRESS;

  hasDifferentMailing = false;
  mailingAddress = DEFAULT_ADDRESS;

  // contact
  email = '';
  phoneNumber = '';

  // disclosures
  isAffiliatedExchangeOrFINRA = false;
  firmName = '';
  isControlPerson = false;
  companySymbols = '';
  isPoliticallyExposed = false;
  politicalOrganization = '';
  immediateFamily = '';

  // foreign specific fields
  foreignCountryOfIssue = null; // "BRA",
  foreignIdentificationNumber = null; // "FY006554",
  foreignIdentificationType = null; // "PASSPORT",
  foreignIdentificationIssueDate = null; // "2020-12-12",
  foreignIdentificationExpirationDate = null; // "2030-12-12"

  // cache (is saved on the application ONLY)
  cache = {
    residentCountry: undefined,
    isSecondaryApplicant: false,
    hasTrustedContact: false,
  };

  // verification object set from the application
  verification;

  constructor(attributes) {
    Object.assign(this, attributes);
  }

  get name() {
    const prefix = this.prefix ? `${this.prefix} ` : '';
    const suffix = this.suffix ? ` ${this.suffix}` : '';

    return `${prefix}${this.firstName} ${this.lastName}${suffix}`;
  }

  get needsIdentificationDates() {
    return this.foreignIdentificationType === 'PASSPORT' || this.foreignIdentificationType === 'DRIVERS_LICENSE';
  }

  get isForeign() {
    return !this.permanentResident && this.citizenshipCountry !== 'USA';
  }

  get residencyCountry() {
    return this.permanentResident || !this.isForeign ? 'USA' : this.cache.residentCountry;
  }

  get isEmployed() {
    return this.employmentStatus === 'EMPLOYED' || this.employmentStatus === 'SELF_EMPLOYED';
  }

  get needsAnnualIncome() {
    return this.employmentStatus !== 'UNEMPLOYED' && this.employmentStatus !== 'STUDENT';
  }

  get age() {
    const birthDate = dayjs(this.dateOfBirth); // Parse the date of birth
    const now = dayjs(); // Current date

    // Calculate the difference in years
    let age = now.year() - birthDate.year();

    // Adjust the age if the birthday hasn't occurred yet this year
    if (now.isBefore(birthDate.add(age, 'year'))) {
      age -= 1;
    }

    return age;
  }

  get hasVerification() {
    return !!this.verification && !!this.verification.scoring;
  }

  get actionRequired() {
    return (
      this.hasVerification &&
      (!this.passedAddressCheck || !this.passedDobCheck || !this.passedOfacCheck || !this.passedSsnCheck)
    );
  }

  get passedOfacCheck() {
    return this.hasVerification && this.verification?.scoring?.ofac?.green;
  }

  get passedAddressCheck() {
    return this.hasVerification && this.verification?.scoring?.address?.green;
  }

  get passedSsnCheck() {
    return this.hasVerification && this.verification?.scoring?.ssn?.green;
  }

  get passedDobCheck() {
    return this.hasVerification && this.verification?.scoring?.dob?.green;
  }

  permanentResidentChange() {
    this.cache.residentCountry = this.permanentResident ? 'USA' : '';
  }

  resetIdentificationDates() {
    this.foreignIdentificationIssueDate = null;
    this.foreignIdentificationExpirationDate = null;
  }
}
